import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Check, ChevronDown, CircleUserRound, Globe, Loader2, LogOut } from "lucide-react";
import { Menu } from "lucide-react";
import { Button } from "@/app/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/app/components/ui/dropdown-menu";
import { LANGUAGES, type Lang } from "@/app/i18n/types";
import { useLanguage } from "@/app/contexts/LanguageContext";
import { useAuth } from "@/app/contexts/AuthContext";
import { apiClient } from "@/app/services/api";
import { cn } from "@/app/utils/tw-merge";
import { ROUTES } from "@/app/config/routes";

/** Emoji quốc kì regional indicator. Dùng native Unicode thay vì asset
 *  để tránh thêm network request và giữ bundle nhỏ. */
const FLAGS: Record<Lang, string> = {
  vi: "🇻🇳",
  en: "🇬🇧",
  ja: "🇯🇵",
};

export function AppHeader({
  onOpenMobileMenu,
}: {
  onOpenMobileMenu: () => void;
}) {
  const { t } = useTranslation();
  const { lang, setLang } = useLanguage();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-transparent backdrop-blur supports-[backdrop-filter]:bg-transparent">
      <div className="flex h-14 md:h-16 items-center gap-2 px-3 md:px-6">
        {/* Hamburger — mobile only */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onOpenMobileMenu}
          className="md:hidden"
          aria-label={t("header.menu")}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Brand */}
        <div className="flex items-center gap-2 md:gap-3">
          <img
            src="/lumina-logo.png"
            alt="Lumina"
            className="h-8 w-8 md:h-9 md:w-9 shrink-0 object-contain"
          />
          <div className="hidden sm:block">
            <h1 className="text-base md:text-xl font-bold text-lumina-gradient">
              Lumina Marketplace
            </h1>
            <p className="hidden md:block text-xs text-muted-foreground">
              {t("header.slogan")}
            </p>
          </div>
        </div>

        {/* Right cluster: session chip + lang */}
        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <SessionChip />
          <LangSwitchInline lang={lang} onSet={setLang} />
        </div>
      </div>
    </header>
  );
}

function SessionChip() {
  const { t } = useTranslation();
  const { user, clearSession } = useAuth();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: async () => {
      await apiClient.post("/api/v1/auth/logout");
    },
    onSettled: () => {
      clearSession();
      navigate(ROUTES.login, { replace: true });
    },
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg border px-2.5 h-8 text-xs font-medium",
            "border-emerald-200 bg-emerald-50 text-emerald-700"
          )}
        >
          <CircleUserRound className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">
            {user?.full_name || user?.email || "User"}
          </span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="min-w-0">
            <div className="truncate text-xs font-medium">
              {user?.full_name || user?.email || "User"}
            </div>
            <div className="truncate text-[11px] font-normal text-muted-foreground">
              {user?.email}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled className="text-xs text-muted-foreground">
          {t(`auth.role.${user?.role ?? "viewer"}` as "auth.role.viewer")}
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
        >
          {logout.isPending ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <LogOut className="mr-2 h-3.5 w-3.5" />
          )}
          {t("auth.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LangSwitchInline({
  lang,
  onSet,
}: {
  lang: Lang;
  onSet: (l: Lang) => void;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
          title={t("lang.switch.title")}
          aria-label={t("lang.switch.title")}
        >
          <Globe className="h-3.5 w-3.5 opacity-70" />
          <span aria-hidden className="text-sm leading-none">{FLAGS[lang]}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t("lang.switch.title")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGES.map((lg) => {
          const active = lg === lang;
          return (
            <DropdownMenuItem
              key={lg}
              onClick={() => onSet(lg)}
              className={cn(
                "cursor-pointer",
                active && "bg-accent text-accent-foreground"
              )}
            >
              <span aria-hidden className="text-base leading-none">{FLAGS[lg]}</span>
              <span className="flex-1">{t(`lang.switch.${lg}` as "lang.switch.vi")}</span>
              {active && <Check className="h-3.5 w-3.5 opacity-80" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
